import { Main as Container } from 'Components/Main';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { getComplianceProgramDashboard } from '../../../services/complice-program';
import { useNavigate } from 'react-router-dom';
import { typeLabelKeys, statusControl, maturityLevelKeys } from '../../../utils/labels_utils';
import { useSelector } from 'react-redux';
import { useAuthTenant } from '../../../store/auth';
import { GapDashboardView } from '../../../Components/GapDashboardView';
import { PageHeader } from '../../../Components/ui';
import styled from 'styled-components';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';

const NotFoundCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 32px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
`;
const NotFoundTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
`;
const NotFoundDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${MUTED};
  margin: 0;
`;
const NotFoundLink = styled.a`
  color: ${BLUE};
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  text-decoration: underline;
`;

const ENTITY_ORDER = ['companies', 'areas', 'processes', 'systems', 'assets'];
const ENTITY_COLOR_MAP = {
  companies: 'var(--vo-cobalto)',
  areas:     'var(--vo-warning)',
  processes: 'var(--vo-celeste)',
  systems:   'var(--vo-capri)',
  assets:    'var(--vo-success)',
};
const MATURITY_COLORS_LIST = ['var(--vo-danger)', 'var(--vo-warning)', 'var(--vo-capri)', 'var(--vo-celeste)', 'var(--vo-success)', 'var(--vo-cobalto)'];

const sortedEntityKeys = obj =>
  ENTITY_ORDER.filter(k => k in obj);

const getStatusLabel = (key, t) => {
  const sc = statusControl[key];
  if (!sc) return key;
  if (sc.labelKey) return t(sc.labelKey);
  return sc.label || key;
};

export const ComplianceProgramDashboard = () => {
  const { t } = useTranslation(['gap-analysis', 'company', 'common']);
  const selectedTenant = useSelector(useAuthTenant);
  const navigate = useNavigate();

  const { isLoading, error, data: d } = useQuery('getComplianceProgramDashboard', async () => {
    try {
      const res = await getComplianceProgramDashboard(selectedTenant.slug);
      return res.status === 200 ? res.data : null;
    } catch (err) {
      if (err?.response?.status === 404) return { hasComplianceProgram: false };
      throw err;
    }
  });

  if (isLoading) return <p>{t('company:complianceProgram.loading')}</p>;
  if (error) return <p>{t('company:complianceProgram.loadingError')}</p>;

  if (d?.hasComplianceProgram === false) {
    return (
      <Container>
        <PageHeader
          title={t('company:complianceProgram.notFound.title')}
          segments={[
            { label: t('common:breadcrumbs.reports') },
            { label: t('company:complianceProgram.breadcrumbLabel') },
          ]}
        />
        <NotFoundCard>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={MUTED} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <NotFoundTitle>{t('company:complianceProgram.notFound.title')}</NotFoundTitle>
          <NotFoundDesc>{t('company:complianceProgram.notFound.description')}</NotFoundDesc>
          <NotFoundLink href="/compliance-program/add">{t('company:complianceProgram.notFound.link')}</NotFoundLink>
        </NotFoundCard>
      </Container>
    );
  }

  const statusDonutData = Object.keys(d.statusCounts || {})
    .map(key => ({
      name: getStatusLabel(key, t),
      value: d.statusCounts[key] || 0,
      color: statusControl[key]?.color || 'var(--vo-slate)',
    }))
    .filter(item => item.value > 0);

  const maturityData = Object.keys(maturityLevelKeys).map((key, i) => ({
    name: t(maturityLevelKeys[key]),
    value: d.maturityCounts?.[key] || 0,
    color: MATURITY_COLORS_LIST[i % MATURITY_COLORS_LIST.length],
  }));

  const entityDonutData = sortedEntityKeys(d.entityCount || {}).map(key => ({
    name: typeLabelKeys[key] ? t(typeLabelKeys[key]) : key,
    value: d.entityCount[key] || 0,
    color: ENTITY_COLOR_MAP[key] || 'var(--vo-slate)',
  }));

  const stackedData = sortedEntityKeys(d.entityCountsByTypeWithStatus || {}).map(key => ({
    name: typeLabelKeys[key] ? t(typeLabelKeys[key]) : key,
    statuses: d.entityCountsByTypeWithStatus[key] || {},
  }));

  const entityProgressRaw = d.entityTypeProcess || {};
  const entityProgressData = Object.values(entityProgressRaw)
    .map(item => ({
      label: typeLabelKeys[item.label] ? t(typeLabelKeys[item.label]) : (item.label || ''),
      value: item.percentage ?? 0,
      _ord: ENTITY_ORDER.indexOf(item.label),
    }))
    .sort((a, b) => (a._ord < 0 ? 999 : a._ord) - (b._ord < 0 ? 999 : b._ord));

  const frameworkData = (d.progressoPorFramework || []).map(item => ({
    label: item.name,
    value: Math.round(item.progress || 0),
  }));

  const entitiesByType = d.entitiesByType || {};
  const tabs = sortedEntityKeys(entitiesByType).map(type => ({ id: type, label: typeLabelKeys[type] ? t(typeLabelKeys[type]) : type }));

  return (
    <GapDashboardView
      title={d.gapAnalysis?.name ?? ''}
      segments={[
        { label: t('common:breadcrumbs.reports') },
        { label: t('company:complianceProgram.breadcrumbLabel'), route: '/compliance-program' },
        { label: d.gapAnalysis?.name ?? '' },
      ]}
      backLabel={t('company:complianceProgram.dashboardBackButton')}
      onBack={() => navigate('/compliance-program')}
      overallProgress={Math.min(d.overallProgress ?? 0, 100)}
      linkedControls={d.linkedControls ?? 0}
      totalControls={d.totalControls ?? 0}
      historicalProgress={d.historicalProgress}
      frameworkData={frameworkData}
      statusDonutData={statusDonutData}
      maturityData={maturityData}
      entityDonutData={entityDonutData}
      stackedData={stackedData}
      entityProgressData={entityProgressData}
      uniqueEntityCount={d.uniqueEntityCount ?? 0}
      entitiesByType={entitiesByType}
      tabs={tabs}
      tableLinkExtend="compliance-program/control/show"
      getTypeLabel={type => typeLabelKeys[type] ? t(typeLabelKeys[type]) : type}
      historicalSubtitle={t('company:complianceProgram.historicalSubtitle')}
    />
  );
};
